// Takes mit Dauer 0 nachdecodieren (Audit M17).
//
// Wechselt das Talent das Asset, während der frisch generierte Take noch
// decodiert, schrieb die Dauer bis 2026-09-24 auf „das aktuelle Asset" und
// blieb damit 0. Resolver, Auto-Position und Zeitlimit-Pill rechneten dann
// mit 0 — Clips überlappten im Mix. Die Schreibstelle ist inzwischen an den
// Asset-Index gebunden; für Sessions, die den Fehler schon in IndexedDB
// tragen, wird beim Restore jeder Take ohne Dauer neu vermessen.
//
// Pure Anteile (Sammeln, Anwenden) sind testbar; das Decodieren übergibt
// der Aufrufer (OfflineAudioContext, kein User-Gesture nötig).

import type { Block, SessionState } from '@shared/types';

export interface ZeroDurationTake {
  /** 'asset' = assetBlocks[idx], 'free-tts' / 'free-sts' = freeBlocks[idx][mode] */
  slot: 'asset' | 'free-tts' | 'free-sts';
  assetIdx: number;
  takeId: string;
  buffer: ArrayBuffer;
}

function scan(blocks: Block[] | undefined, slot: ZeroDurationTake['slot'], assetIdx: number, out: ZeroDurationTake[]) {
  for (const b of blocks ?? []) {
    for (const t of b.takes) {
      if ((t.duration ?? 0) > 0) continue;
      if (!t.buffer || t.buffer.byteLength === 0) continue;
      out.push({ slot, assetIdx, takeId: t.id, buffer: t.buffer });
    }
  }
}

/** Alle Takes ohne Dauer, mit Fundort. */
export function collectZeroDurationTakes(s: SessionState): ZeroDurationTake[] {
  const out: ZeroDurationTake[] = [];
  for (const [k, blocks] of Object.entries(s.assetBlocks ?? {})) scan(blocks, 'asset', Number(k), out);
  for (const [k, modes] of Object.entries(s.freeBlocks ?? {})) {
    scan(modes?.tts, 'free-tts', Number(k), out);
    scan(modes?.sts, 'free-sts', Number(k), out);
  }
  return out;
}

function patch(blocks: Block[] | undefined, takeId: string, duration: number): Block[] | undefined {
  if (!blocks) return blocks;
  const bi = blocks.findIndex((b) => b.takes.some((t) => t.id === takeId));
  if (bi === -1) return blocks;
  const target = blocks[bi];
  const takes = target.takes.map((t) => (t.id === takeId ? { ...t, duration } : t));
  const next = blocks.slice();
  next[bi] = { ...target, takes };
  return next;
}

/** Trägt gemessene Dauern ein. Gibt für Slots ohne Änderung dieselben
 *  Referenzen zurück; `blocks` (Spiegel des aktiven Assets) folgt
 *  `assetBlocks[currentAssetIdx]`. */
export function applyTakeDurations(
  s: SessionState,
  measured: Array<{ item: ZeroDurationTake; duration: number }>,
): SessionState {
  if (measured.length === 0) return s;
  const assetBlocks = { ...s.assetBlocks };
  const freeBlocks = { ...s.freeBlocks };
  for (const { item, duration } of measured) {
    if (!(duration > 0)) continue;
    if (item.slot === 'asset') {
      const next = patch(assetBlocks[item.assetIdx], item.takeId, duration);
      if (next) assetBlocks[item.assetIdx] = next;
    } else {
      const mode = item.slot === 'free-tts' ? 'tts' : 'sts';
      const cur = freeBlocks[item.assetIdx];
      if (!cur) continue;
      const next = patch(cur[mode], item.takeId, duration);
      if (next) freeBlocks[item.assetIdx] = { ...cur, [mode]: next };
    }
  }
  return {
    ...s,
    assetBlocks,
    freeBlocks,
    blocks: assetBlocks[s.currentAssetIdx] ?? s.blocks,
  };
}

/** Dauer eines MP3-Puffers über einen OfflineAudioContext — braucht keine
 *  User-Gesture und keinen laufenden Kontext. `.slice(0)`, weil
 *  decodeAudioData den Puffer konsumiert. */
export async function probeAudioDuration(buffer: ArrayBuffer): Promise<number> {
  const ctx = new OfflineAudioContext(1, 1, 44100);
  const decoded = await ctx.decodeAudioData(buffer.slice(0));
  return decoded.duration;
}
